import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages
import HomePage from "./pages/HomePage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticlePage from "./pages/ArticlePage";
import AuthorsPage from "./pages/AuthorsPage";
import AuthorPage from "./pages/AuthorPage";
import AboutPage from "./pages/AboutPage";
import SearchPage from "./pages/SearchPage";
import AdminPage from "./pages/AdminPage";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth callback component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
            withCredentials: true
          });
          // Clear hash and navigate to home with user data
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/', { state: { user: response.data }, replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/', { replace: true });
        }
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="font-mono text-sm text-muted-foreground">Authenticating...</div>
    </div>
  );
};

// Wrapper component that handles auth callback routing
function AuthRouter() {
  const location = useLocation();
  
  // Check for session_id in URL hash synchronously - before any hooks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppRouter />;
}

// App router with auth detection
function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);

  useEffect(() => {
    // Skip auth check if we already have user from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setAuthChecked(true);
      return;
    }

    // Check existing session
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        // Not authenticated, that's fine
      }
      setAuthChecked(true);
    };

    checkAuth();
  }, [location.state]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, authChecked }}>
      <div className="min-h-screen bg-background flex flex-col">
        <div className="noise-overlay" />
        <Navbar />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
            <Route path="/category/:slug" element={<ArticlesPage />} />
            <Route path="/authors" element={<AuthorsPage />} />
            <Route path="/authors/:authorId" element={<AuthorPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        <Footer />
        <Toaster position="bottom-right" />
      </div>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthRouter />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
